// img_crop_v1: ครอป/ซูม/ปรับตำแหน่งรูป ล็อกสัดส่วน แล้วส่งออกเป็น Blob (canvas, ไม่พึ่ง lib ภายนอก)
import React, { useEffect, useRef, useState, useCallback } from 'react';

export default function ImageCropModal({ file, aspect = 20 / 13, outWidth = 1040, onCancel, onDone }) {
  const FRAME_W = 360;
  const FRAME_H = Math.round(FRAME_W / aspect);
  const OUT_W = outWidth;
  const OUT_H = Math.round(outWidth / aspect);

  const [imgEl, setImgEl] = useState(null);
  const [nat, setNat] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const drag = useRef(null);
  const urlRef = useRef(null);

  const baseScale = nat.w ? Math.max(FRAME_W / nat.w, FRAME_H / nat.h) : 1;
  const effScale = baseScale * zoom;
  const dispW = nat.w * effScale;
  const dispH = nat.h * effScale;

  const clamp = useCallback((o, dW, dH) => ({
    x: Math.min(0, Math.max(FRAME_W - dW, o.x)),
    y: Math.min(0, Math.max(FRAME_H - dH, o.y)),
  }), [FRAME_W, FRAME_H]);

  // โหลดรูปจาก file
  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    urlRef.current = url;
    const im = new Image();
    im.onload = () => {
      setImgEl(im);
      setNat({ w: im.naturalWidth, h: im.naturalHeight });
      const bs = Math.max(FRAME_W / im.naturalWidth, FRAME_H / im.naturalHeight);
      const dW = im.naturalWidth * bs, dH = im.naturalHeight * bs;
      setZoom(1);
      setOffset({ x: (FRAME_W - dW) / 2, y: (FRAME_H - dH) / 2 }); // จัดกึ่งกลาง
    };
    im.src = url;
    return () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  // ซูม: ขยายรอบจุดกึ่งกลางเฟรม
  const onZoom = (z) => {
    const oldEff = baseScale * zoom;
    const newEff = baseScale * z;
    const cx = FRAME_W / 2, cy = FRAME_H / 2;
    const imgX = (cx - offset.x) / oldEff;
    const imgY = (cy - offset.y) / oldEff;
    const nx = cx - imgX * newEff;
    const ny = cy - imgY * newEff;
    setZoom(z);
    setOffset(clamp({ x: nx, y: ny }, nat.w * newEff, nat.h * newEff));
  };

  const onPointerDown = (e) => { drag.current = { px: e.clientX, py: e.clientY, ox: offset.x, oy: offset.y }; try { e.target.setPointerCapture(e.pointerId); } catch (_) {} };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const nx = drag.current.ox + (e.clientX - drag.current.px);
    const ny = drag.current.oy + (e.clientY - drag.current.py);
    setOffset(clamp({ x: nx, y: ny }, dispW, dispH));
  };
  const onPointerUp = () => { drag.current = null; };

  const confirm = () => {
    if (!imgEl || busy) return;
    setBusy(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = OUT_W; canvas.height = OUT_H;
      const ctx = canvas.getContext('2d');
      // เฟรม (0,0)-(FRAME_W,FRAME_H) map กลับเป็นพิกัดรูปต้นฉบับ
      const sx = (-offset.x) / effScale;
      const sy = (-offset.y) / effScale;
      const sw = FRAME_W / effScale;
      const sh = FRAME_H / effScale;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(imgEl, sx, sy, sw, sh, 0, 0, OUT_W, OUT_H);
      canvas.toBlob((blob) => {
        setBusy(false);
        if (blob) onDone && onDone(blob);
      }, 'image/jpeg', 0.9);
    } catch (e) { setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#00000070', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 20, width: 'min(440px, 96vw)', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: '#1e293b' }}>✂️ ปรับ / ครอปรูป</h3>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: '#94a3b8' }}>ลากเพื่อเลื่อน · เลื่อนแถบเพื่อซูม · ส่งออก {OUT_W}×{OUT_H}px (สัดส่วน 20:13)</p>

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
          <div
            onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
            style={{ position: 'relative', width: FRAME_W, height: FRAME_H, overflow: 'hidden', borderRadius: 10, background: '#0f172a', cursor: 'move', touchAction: 'none', userSelect: 'none', border: '2px solid #0ea5e9' }}>
            {imgEl && (
              <img src={imgEl.src} alt="" draggable={false}
                style={{ position: 'absolute', left: offset.x, top: offset.y, width: dispW, height: dispH, maxWidth: 'none', pointerEvents: 'none' }} />
            )}
            {/* เส้นกริดช่วยจัดองค์ประกอบ */}
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: 'linear-gradient(#ffffff40 1px,transparent 1px),linear-gradient(90deg,#ffffff40 1px,transparent 1px)', backgroundSize: `${FRAME_W / 3}px ${FRAME_H / 3}px` }} />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <span style={{ fontSize: 18 }}>🔍</span>
          <input type="range" min="1" max="3" step="0.01" value={zoom} onChange={(e) => onZoom(parseFloat(e.target.value))} style={{ flex: 1 }} />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onCancel} disabled={busy}
            style={{ flex: 1, padding: 11, background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>ยกเลิก</button>
          <button onClick={confirm} disabled={busy || !imgEl}
            style={{ flex: 1, padding: 11, background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 8, cursor: busy ? 'wait' : 'pointer', fontWeight: 700 }}>{busy ? 'กำลังประมวลผล...' : 'ใช้รูปนี้'}</button>
        </div>
      </div>
    </div>
  );
}
